import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { CustomInputBox1, WSButton } from '../Templates.js';
import Spinner from '../common/Spinner';
import './MyProfile.css';

const CarManager = ({ cars, onAddCar, onDeleteCar, isLoading }) => {
  const { t } = useTranslation('common');
  const [showAddCar, setShowAddCar] = useState(false);
  const [carFormData, setCarFormData] = useState({
    brand: '',
    model: '',
    year: '',
    number: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleCarInputChange = (field, value) => {
    setCarFormData(prev => ({
      ...prev,
      [field]: field === 'number' ? value.toUpperCase() : value
    }));
    
    // Clear validation errors when typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateCarForm = () => {
    const errors = {};
    
    if (carFormData.brand && !carFormData.number) {
      errors.number = t('profile.number_required');
    }
    
    if (!carFormData.brand && carFormData.number) {
      errors.brand = t('profile.brand_required');
    }
    
    if (!carFormData.brand && !carFormData.number) {
      errors.brand = t('profile.brand_and_number_required');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCar = async () => {
    if (!validateCarForm()) {
      return;
    }
    
    try {
      await onAddCar(carFormData);
      
      // Reset form
      setCarFormData({
        brand: '',
        model: '',
        year: '',
        number: ''
      });
      
      setShowAddCar(false);
    } catch (error) {
      console.error('Error adding car:', error);
    }
  };

  return (
    <div className="cars-section">
      <h4>{t('profile.your_cars')}:</h4>
      <div className="cars-list">
        {cars.length > 0 ? (
          cars.map((car) => (
            <div key={car.id} className="car-item">
              <span>{car.brand} {car.model} {car.year && `(${car.year})`} - {car.number}</span>
              <WSButton
                variant="icon"
                danger
                onClick={() => onDeleteCar(car.id)}
                disabled={isLoading}
                ariaLabel={t('buttons.delete') || 'Delete car'}
                icon={<FaTrash size={14} />}
              />
            </div>
          ))
        ) : (
          <p>{t('profile.no_cars')}</p>
        )}
      </div>

      <div className="Car-box">
        <WSButton
          variant={showAddCar ? 'secondary' : 'primary'}
          label={showAddCar ? t('buttons.cancel') : t('profile.add_car')}
          onClick={() => setShowAddCar((prev) => !prev)}
          icon={showAddCar ? <FaTimes /> : <FaPlus />}
        />
      </div>
      
      {showAddCar && (
        <div className="addcar-box">
          <div className="car-form-field">
            <CustomInputBox1
              className="wsform-input"
              placeholder={t('profile.car_brand')}
              value={carFormData.brand}
              onChange={(e) => handleCarInputChange('brand', e.target.value)}
            />
            {validationErrors.brand && (
              <div className="validation-error">{validationErrors.brand}</div>
            )}
          </div>

          <div className="car-form-field">
            <CustomInputBox1
              className="wsform-input"
              placeholder={t('profile.car_model')}
              value={carFormData.model}
              onChange={(e) => handleCarInputChange('model', e.target.value)}
            />
          </div>

          <div className="car-form-field">
            <CustomInputBox1
              className="wsform-input"
              placeholder={t('profile.year')}
              value={carFormData.year}
              onChange={(e) => handleCarInputChange('year', e.target.value)}
            />
          </div>

          <div className="car-form-field">
            <CustomInputBox1
              className="wsform-input"
              placeholder={t('profile.plate_number')}
              value={carFormData.number}
              onChange={(e) => handleCarInputChange('number', e.target.value)}
            />
            {validationErrors.number && (
              <div className="validation-error">{validationErrors.number}</div>
            )}
          </div>

          <WSButton
            variant="primary"
            label={t('buttons.add')}
            onClick={handleAddCar}
            disabled={isLoading}
            icon={isLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaPlus />}
          />
        </div>
      )}
    </div>
  );
};

export default CarManager;